import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Lock, Camera } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrandAndContact } from "@/hooks/useBrandAndContact";
import ThemeToggle from "@/components/ThemeToggle";

const Navbar = () => {
  const { t, lang, toggleLang } = useLanguage();
  const CONTACT = useBrandAndContact();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu when navigating to a new path.
  useEffect(() => {
    queueMicrotask(() => setOpen(false));
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), testId: "nav-link-home" },
    { to: "/services", label: t("nav.services"), testId: "nav-link-services" },
    { to: "/formation", label: t("nav.training"), testId: "nav-link-training" },
    { to: "/a-propos", label: t("nav.about"), testId: "nav-link-about" },
    { to: "/contact", label: t("nav.contact"), testId: "nav-link-contact" },
  ];

  // Photothèque shortcut — featured CTA placed right before "Contact" so
  // event guests find their photos in one click. The label is intentionally
  // shorter on desktop (where space is tight) than on mobile/title attr.
  const photothequeFullLabel =
    lang === "fr"
      ? "Retrouvez vos photos d'évent ici"
      : "Find your event photos here";
  const photothequeShortLabel =
    lang === "fr" ? "Mes photos d'évent" : "My event photos";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "backdrop-blur-xl bg-[#050505]/85 border-b border-[#C5A059]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 h-20 flex items-center justify-between gap-3">
        <Link
          to="/"
          data-testid="nav-brand-logo"
          className="flex items-center group flex-shrink-0"
        >
          <img
            src="/brand/kiwami-logo.png"
            alt="Kiwami Studios & Co"
            className="h-7 md:h-8 w-auto select-none flex-shrink-0"
            draggable={false}
          />
          <span className="hidden md:inline-block ml-4 pl-4 border-l border-[#C5A059]/30 text-[10px] uppercase tracking-[0.3em] text-[#A19E9C] group-hover:text-[#C5A059] transition-colors">
            Studios &amp; Co
          </span>
        </Link>

        {/* Desktop nav — tightened from gap-10 → gap-7 to make room for the
            Photothèque shortcut without crowding the right-side actions. */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {links.map((l, idx) => {
            // Inject the Photothèque CTA right before the "Contact" link.
            const isContact = l.to === "/contact";
            return (
              <React.Fragment key={l.to}>
                {isContact && (
                  <NavLink
                    to="/services/phototheque"
                    data-testid="nav-link-phototheque"
                    title={photothequeFullLabel}
                    className={({ isActive }) =>
                      `group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] xl:text-[11px] font-medium tracking-[0.18em] whitespace-nowrap transition-colors ${
                        isActive
                          ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                          : "border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 hover:border-[#C5A059]"
                      }`
                    }
                  >
                    <Camera className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                    <span>{photothequeShortLabel}</span>
                  </NavLink>
                )}
                <NavLink
                  to={l.to}
                  data-testid={l.testId}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `text-[11px] uppercase tracking-[0.28em] font-medium transition-colors ${
                      isActive
                        ? "text-[#C5A059]"
                        : "text-[#F2EBE5] hover:text-[#C5A059]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          <ThemeToggle />

          <button
            type="button"
            data-testid="lang-toggle-btn"
            onClick={toggleLang}
            className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.3em] font-medium text-[#F2EBE5] hover:text-[#C5A059] transition-colors border border-[#F2EBE5]/15 px-2.5 md:px-3 py-1.5 rounded-full whitespace-nowrap"
          >
            <span className={lang === "fr" ? "text-[#C5A059]" : ""}>FR</span>
            <span className="mx-1 md:mx-2 text-[#F2EBE5]/30">/</span>
            <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
          </button>

          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            title={lang === "fr" ? "Espace administrateur" : "Admin area"}
            aria-label={lang === "fr" ? "Espace administrateur" : "Admin area"}
            className="hidden md:inline-flex items-center justify-center w-9 h-9 border border-[#F2EBE5]/15 rounded-full text-[#F2EBE5] hover:text-[#C5A059] hover:border-[#C5A059]/50 transition-colors"
          >
            <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>

          <Link
            to="/contact"
            data-testid="nav-order-cta"
            className="!hidden lg:!inline-flex kw-btn-primary !py-1.5 !px-4 !text-[10px] !tracking-[0.2em] !gap-2"
          >
            {t("nav.order")}
          </Link>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[#F2EBE5] hover:text-[#C5A059] transition-colors"
          >
            {open ? (
              <X className="w-6 h-6" strokeWidth={1.25} />
            ) : (
              <Menu className="w-6 h-6" strokeWidth={1.25} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-[#C5A059]/10 bg-[#050505]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {links.map((l) => {
              const isContact = l.to === "/contact";
              return (
                <React.Fragment key={l.to}>
                  {isContact && (
                    <NavLink
                      to="/services/phototheque"
                      data-testid="nav-link-phototheque-mobile"
                      className={({ isActive }) =>
                        `inline-flex items-center gap-3 px-4 py-3 rounded-full border self-start max-w-full text-sm font-medium transition-colors ${
                          isActive
                            ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10"
                            : "border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10"
                        }`
                      }
                    >
                      <Camera className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                      <span className="truncate">{photothequeFullLabel}</span>
                    </NavLink>
                  )}
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`${l.testId}-mobile`}
                    className={({ isActive }) =>
                      `font-serif-display text-3xl italic transition-colors ${
                        isActive ? "text-[#C5A059]" : "text-[#F2EBE5]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </React.Fragment>
              );
            })}
            <div className="pt-6 border-t border-[#F2EBE5]/10 space-y-4">
              <a
                href={CONTACT.phoneHref}
                className="flex items-center gap-3 text-sm text-[#F2EBE5] hover:text-[#C5A059] transition-colors"
                data-testid="mobile-phone-link"
              >
                <Phone className="w-4 h-4 text-[#C5A059]" strokeWidth={1.25} />
                {CONTACT.phone}
              </a>
              <Link
                to="/contact"
                data-testid="mobile-order-cta"
                className="kw-btn-primary"
              >
                {t("nav.order")}
              </Link>
              <Link
                to="/admin/login"
                data-testid="mobile-admin-link"
                className="flex items-center gap-3 text-sm text-[#A19E9C] hover:text-[#C5A059] transition-colors pt-2"
              >
                <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
                {lang === "fr" ? "Espace administrateur" : "Admin area"}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
